import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Users,
  Shield,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ChevronRight,
  User,
  Check,
} from "lucide-react";
import { toast } from "sonner";

// ── Page metadata ─────────────────────────────────────────────────────────────
const PAGE_META = {
  dashboard: { label: "Dashboard", desc: "Overview & stats" },
  leads: { label: "Leads", desc: "Lead management" },
  clients: { label: "Clients", desc: "Client directory" },
  meetings: { label: "Meetings", desc: "Schedule meetings" },
  shoots: { label: "Shoots", desc: "Photo/video shoots" },
  editing: { label: "Editing", desc: "Content editing tasks" },
  ads: { label: "Ads", desc: "Ad campaign tracking" },
  team: { label: "Team", desc: "Team management" },
  activity: { label: "Activity", desc: "Activity log" },
  settings: { label: "Settings", desc: "Account settings" },
  work: { label: "My Work", desc: "Assigned work and approvals" },
  social: { label: "Social Media", desc: "Social content planning" },
};

const ALL_PAGES = Object.keys(PAGE_META);

const ROLE_BADGE_COLORS = {
  owner: "bg-rose-100 text-rose-800 border-rose-200",
  pillar: "bg-amber-100 text-amber-800 border-amber-200",
  employee: "bg-sky-100 text-sky-800 border-sky-200",
};

function roleBadgeClass(role) {
  return (
    ROLE_BADGE_COLORS[role] ?? "bg-slate-100 text-slate-700 border-slate-200"
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TeamPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        apiClient.get("/users"),
        apiClient.get("/roles"),
      ]);
      setUsers(uRes.data);
      setRoles(rRes.data);
    } catch {
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <p className="text-muted-foreground p-6">Loading team data…</p>;
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      <div>
        <h1 className="text-4xl font-bold" style={{ letterSpacing: "-0.02em" }}>
          Team
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your team members and access roles
        </p>
      </div>

      <Tabs defaultValue="members" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="members" className="flex items-center gap-2">
            <Users className="w-4 h-4" /> Members
            <span className="ml-1 text-xs bg-muted rounded-full px-2 py-0.5">
              {users.length}
            </span>
          </TabsTrigger>
          {isOwner && (
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Shield className="w-4 h-4" /> Roles &amp; Permissions
              <span className="ml-1 text-xs bg-muted rounded-full px-2 py-0.5">
                {roles.length}
              </span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Members tab ─────────────────────────────────────────────────── */}
        <TabsContent value="members">
          <MembersTab
            users={users}
            roles={roles}
            isOwner={isOwner}
            onRefresh={fetchAll}
          />
        </TabsContent>

        {/* ── Roles tab ───────────────────────────────────────────────────── */}
        {isOwner && (
          <TabsContent value="roles">
            <RolesTab roles={roles} onRefresh={fetchAll} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ── Members Tab ───────────────────────────────────────────────────────────────
function MembersTab({ users, roles, isOwner, onRefresh }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const blankForm = { name: "", email: "", password: "", role: "", phone: "" };
  const [form, setForm] = useState(blankForm);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.role) {
      toast.error("Please select a role");
      return;
    }
    try {
      await apiClient.post("/users", {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        phone: form.phone || undefined,
      });
      toast.success(`Account created for ${form.name}`);
      setAddOpen(false);
      setForm(blankForm);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create account");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await apiClient.patch(`/users/${userId}/role`, { role: newRole });
      toast.success("Role updated");
      setEditUserId(null);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update role");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {users.length} member{users.length !== 1 ? "s" : ""} in your agency
        </p>
        {isOwner && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Team Member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Team Account</DialogTitle>
                <DialogDescription>
                  This person will receive login credentials to access AgencyOS.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleAdd} className="space-y-4 pt-2">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    placeholder="Jane Smith"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="jane@agency.com"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    required
                  />
                </div>

                <div>
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPwd ? "text" : "password"}
                      placeholder="Set a temporary password"
                      value={form.password}
                      onChange={(e) =>
                        setForm({ ...form, password: e.target.value })
                      }
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPwd(!showPwd)}
                    >
                      {showPwd ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <Label>Phone (optional)</Label>
                  <Input
                    type="tel"
                    placeholder="+919876543210"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Include country code for WhatsApp reminders
                  </p>
                </div>

                <div>
                  <Label>Role *</Label>
                  <select
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    required
                  >
                    <option value="">— Select a role —</option>
                    {roles
                      .filter((r) => r.name !== "owner")
                      .map((r) => (
                        <option key={r.name} value={r.name}>
                          {r.display_name}
                        </option>
                      ))}
                  </select>
                </div>

                <Button type="submit" className="w-full">
                  Create Account
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Member cards */}
      <div className="grid gap-3">
        {users.map((member) => {
          const isEditing = editUserId === member.id;
          return (
            <Card key={member.id} className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-primary" />
                  </div>

                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">
                      {member.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {member.email}
                    </p>
                    {member.phone && (
                      <p className="text-xs text-muted-foreground">
                        {member.phone}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {isEditing ? (
                    /* Inline role switcher */
                    <div className="flex items-center gap-2">
                      <select
                        className="h-8 rounded-md border border-input bg-transparent px-2 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        defaultValue={member.role}
                        onChange={(e) =>
                          handleRoleChange(member.id, e.target.value)
                        }
                      >
                        {roles.map((r) => (
                          <option key={r.name} value={r.name}>
                            {r.display_name}
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditUserId(null)}
                        className="text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Badge
                        className={`text-xs border ${roleBadgeClass(member.role)}`}
                      >
                        {roles.find((r) => r.name === member.role)
                          ?.display_name ?? member.role}
                      </Badge>
                      {isOwner && member.role !== "owner" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          title="Change role"
                          onClick={() => setEditUserId(member.id)}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Pages this member can access */}
              {isOwner && !isEditing && (
                <div className="mt-3 pt-3 border-t flex flex-wrap gap-1.5">
                  {(roles.find((r) => r.name === member.role)?.pages ?? []).map(
                    (pg) => (
                      <span
                        key={pg}
                        className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                      >
                        {PAGE_META[pg]?.label ?? pg}
                      </span>
                    ),
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Roles Tab ─────────────────────────────────────────────────────────────────
function RolesTab({ roles, onRefresh }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState(null); // role object being edited

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Define what each role can see and do inside AgencyOS
        </p>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Role
        </Button>
      </div>

      {/* Role cards */}
      <div className="grid gap-4">
        {roles.map((role) => (
          <RoleCard
            key={role.name}
            role={role}
            onEdit={() => setEditRole(role)}
            onDelete={async () => {
              if (!window.confirm(`Delete role "${role.display_name}"?`))
                return;
              try {
                await apiClient.delete(`/roles/${role.name}`);
                toast.success("Role deleted");
                onRefresh();
              } catch (err) {
                toast.error(
                  err?.response?.data?.detail || "Failed to delete role",
                );
              }
            }}
            onRefresh={onRefresh}
          />
        ))}
      </div>

      {/* Create Role Dialog */}
      <RoleFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onRefresh={onRefresh}
        mode="create"
      />

      {/* Edit Role Dialog */}
      {editRole && (
        <RoleFormDialog
          open={!!editRole}
          onClose={() => setEditRole(null)}
          onRefresh={onRefresh}
          mode="edit"
          existing={editRole}
        />
      )}
    </div>
  );
}

// ── Role Card ─────────────────────────────────────────────────────────────────
function RoleCard({ role, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="overflow-hidden">
      <div className="p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Shield className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-sm">{role.display_name}</p>
              {role.is_system && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                  system
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {role.pages?.length ?? 0} page
              {role.pages?.length !== 1 ? "s" : ""} accessible
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setExpanded((p) => !p)}
            title="View pages"
          >
            <ChevronRight
              className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </Button>
          {!role.is_system && (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={onEdit}
                title="Edit role"
              >
                <Pencil className="w-3 h-3" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={onDelete}
                title="Delete role"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t px-4 py-3 bg-muted/30">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Accessible pages
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {ALL_PAGES.map((pg) => {
              const has = role.pages?.includes(pg);
              return (
                <div
                  key={pg}
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs
                    ${
                      has
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-slate-50 text-slate-400 border border-slate-200 line-through"
                    }`}
                >
                  {has ? (
                    <Check className="w-3 h-3 shrink-0" />
                  ) : (
                    <EyeOff className="w-3 h-3 shrink-0" />
                  )}
                  {PAGE_META[pg]?.label ?? pg}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Role Form Dialog (create / edit) ─────────────────────────────────────────
function RoleFormDialog({ open, onClose, onRefresh, mode, existing }) {
  const isEdit = mode === "edit";

  const [name, setName] = useState(existing?.name ?? "");
  const [displayName, setDisplayName] = useState(existing?.display_name ?? "");
  const [pages, setPages] = useState(new Set(existing?.pages ?? []));
  const [saving, setSaving] = useState(false);

  // Keep state fresh when dialog re-opens for a different role
  useEffect(() => {
    setName(existing?.name ?? "");
    setDisplayName(existing?.display_name ?? "");
    setPages(new Set(existing?.pages ?? []));
  }, [existing, open]);

  const togglePage = (pg) => {
    setPages((prev) => {
      const next = new Set(prev);
      next.has(pg) ? next.delete(pg) : next.add(pg);
      return next;
    });
  };

  const selectAll = () => setPages(new Set(ALL_PAGES));
  const clearAll = () => setPages(new Set());

  const handleSave = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      toast.error("Role name is required");
      return;
    }
    if (pages.size === 0) {
      toast.error("Select at least one page");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.patch(`/roles/${existing.name}`, {
          display_name: displayName.trim(),
          pages: Array.from(pages),
        });
        toast.success("Role updated");
      } else {
        await apiClient.post("/roles", {
          name: name.trim().toLowerCase().replace(/\s+/g, "_"),
          display_name: displayName.trim(),
          pages: Array.from(pages),
        });
        toast.success("Role created");
      }
      onClose();
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `Edit Role: ${existing?.display_name}`
              : "Create New Role"}
          </DialogTitle>
          <DialogDescription>
            Choose which pages this role can access in AgencyOS.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-5 pt-2">
          {/* Role internal name — only on create */}
          {!isEdit && (
            <div>
              <Label>Role ID (slug) *</Label>
              <Input
                placeholder="e.g. video_editor"
                value={name}
                onChange={(e) =>
                  setName(
                    e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                  )
                }
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lowercase letters, numbers and underscores only
              </p>
            </div>
          )}

          <div>
            <Label>Display Name *</Label>
            <Input
              placeholder="e.g. Video Editor"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>

          {/* Page permissions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Page Access *</Label>
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={selectAll}
                >
                  Select all
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:underline"
                  onClick={clearAll}
                >
                  Clear all
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ALL_PAGES.map((pg) => {
                const checked = pages.has(pg);
                return (
                  <label
                    key={pg}
                    className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors
                      ${
                        checked
                          ? "border-primary bg-primary/5"
                          : "border-input hover:bg-muted/50"
                      }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-primary"
                      checked={checked}
                      onChange={() => togglePage(pg)}
                    />
                    <div>
                      <p className="text-sm font-medium leading-none">
                        {PAGE_META[pg]?.label ?? pg}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {PAGE_META[pg]?.desc ?? ""}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground mt-2">
              {pages.size} of {ALL_PAGES.length} pages selected
            </p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Role"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
